import { useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { format } from "date-fns";
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import {
  useDocument,
  useDocumentChunks,
  useDocumentRetrievalStats,
  useUploadNewVersion,
  useVersionHistory,
} from "../../features/documents/hooks";
import { trpc } from "../../lib/api/trpc";
import type {
  DocumentChunksResponse,
  VersionHistoryEntry,
} from "../../features/documents/hooks";
import type { DocumentStatus } from "../../types/documents";
import { useSupportedFormats } from "../../features/documents/hooks/useSupportedFormats";
import { useReprocessDocument } from "../../features/documents/hooks/useReprocessDocument";
import { usePermissions } from "../../hooks/usePermissions";

/**
 * Format file size in human-readable format
 */
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

/** Shared card shell classes. */
const CARD = "rounded-[14px] border border-[var(--border)] bg-[var(--surface)]";

/**
 * Token-based status pill with a colored leading dot.
 */
function StatusPill({ status }: { status: DocumentStatus }) {
  const styles: Record<string, { wrap: string; dot: string; label?: string }> =
    {
      INDEXED: {
        wrap: "bg-[var(--ok-bg)] text-[var(--ok-text)]",
        dot: "bg-[var(--ok-dot)]",
      },
      QUEUED: {
        wrap: "bg-[var(--warn-bg)] text-[var(--warn-text)]",
        dot: "bg-[var(--warn-dot)]",
      },
      PROCESSING: {
        wrap: "bg-[var(--warn-bg)] text-[var(--warn-text)]",
        dot: "bg-[var(--warn-dot)]",
      },
      PROCESSED: {
        wrap: "bg-[var(--warn-bg)] text-[var(--warn-text)]",
        dot: "bg-[var(--warn-dot)]",
      },
      PRESIGNED: {
        wrap: "bg-[var(--warn-bg)] text-[var(--warn-text)]",
        dot: "bg-[var(--warn-dot)]",
      },
      PARTIALLY_INDEXED: {
        wrap: "bg-[var(--warn-bg)] text-[var(--warn-text)]",
        dot: "bg-[var(--warn-dot)]",
      },
      FAILED: {
        wrap: "bg-[var(--err-bg)] text-[var(--err-text)]",
        dot: "bg-[var(--err-dot)]",
      },
      ARCHIVED: {
        wrap: "bg-[var(--surface-2)] text-[var(--text-3)]",
        dot: "bg-[var(--text-faint)]",
      },
    };
  const s = styles[status] ?? styles.ARCHIVED;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${s.wrap}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {status.replace(/_/g, " ")}
    </span>
  );
}

/**
 * Get status description text
 */
const getStatusDescription = (status: DocumentStatus): string => {
  switch (status) {
    case "PRESIGNED":
      return "Presigned upload URL issued. Waiting for file to be uploaded to S3.";
    case "QUEUED":
      return "Document has been uploaded and is queued for processing.";
    case "PROCESSING":
      return "Document is currently being processed. Content is being extracted and chunked.";
    case "PROCESSED":
      return "Document has been processed and chunks have been created. Ready for indexing.";
    case "INDEXED":
      return "Document is fully indexed and available for search and chat queries.";
    case "PARTIALLY_INDEXED":
      return "Some chunks failed to embed, so this document is only partially searchable.";
    case "FAILED":
      return "Document processing failed. Please check the logs or try uploading again.";
    default:
      return "Unknown status";
  }
};

/**
 * Get next action based on status
 */
const getNextAction = (
  status: DocumentStatus,
  navigate: (path: string) => void,
): { label: string; onClick: () => void; variant: string } | null => {
  switch (status) {
    case "INDEXED":
      return {
        label: "Search Document",
        onClick: () => navigate("/search"),
        variant: "primary",
      };
    case "FAILED":
      return {
        label: "Upload New Version",
        onClick: () => navigate("/documents/upload"),
        variant: "secondary",
      };
    default:
      return null;
  }
};

/**
 * Processing timeline stages
 */
const getTimelineStages = (status: DocumentStatus) => {
  const stages = [
    { key: "PRESIGNED", label: "Presigned" },
    { key: "QUEUED", label: "Queued" },
    { key: "PROCESSING", label: "Processing" },
    { key: "PROCESSED", label: "Processed" },
    { key: "INDEXED", label: "Indexed" },
  ];

  const statusOrder: Record<string, number> = {
    PRESIGNED: 0,
    QUEUED: 1,
    PROCESSING: 2,
    PROCESSED: 3,
    INDEXED: 4,
    FAILED: -1,
  };

  const currentIndex = statusOrder[status] ?? -1;
  const isTerminalComplete = status === "INDEXED";

  return stages.map((stage, index) => ({
    ...stage,
    completed: isTerminalComplete
      ? index <= currentIndex
      : index < currentIndex,
    current: !isTerminalComplete && index === currentIndex,
    upcoming: index > currentIndex,
  }));
};

/**
 * Skeleton placeholder for card sections
 */
function CardSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className={`${CARD} p-[18px_20px] animate-pulse`}>
      <div className="h-5 w-40 bg-[var(--surface-2)] rounded-[7px] mb-4" />
      <div className="space-y-3">
        {Array.from({ length: rows }, (_, i) => (
          <div
            key={i}
            className="h-4 w-full bg-[var(--surface-2)] rounded-[7px]"
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Retrieval activity stats card
 */
function RetrievalStatsCard({ documentId }: { documentId: string }) {
  const { stats, isLoading, isError } = useDocumentRetrievalStats(documentId);

  if (isLoading) return <CardSkeleton rows={2} />;

  if (isError) {
    return (
      <div className={`${CARD} p-[16px_18px]`}>
        <h2 className="text-[14px] font-semibold text-[var(--text)] mb-[10px]">
          Retrieval activity
        </h2>
        <p className="text-[12.5px] text-[var(--err-text)]">
          Failed to load retrieval stats.
        </p>
      </div>
    );
  }

  return (
    <div className={`${CARD} p-[16px_18px]`}>
      <h2 className="text-[14px] font-semibold text-[var(--text)] mb-[10px]">
        Retrieval activity
      </h2>
      <div className="flex justify-between text-center mb-3">
        <div className="flex-1">
          <div className="font-serif text-[26px] font-medium text-[var(--text)]">
            {stats?.totalCitations ?? 0}
          </div>
          <div className="text-[11px] text-[var(--text-3)] mt-1">
            Cited in answers
          </div>
        </div>
        <div className="w-px bg-[var(--border-2)]" />
        <div className="flex-1">
          <div className="font-serif text-[26px] font-medium text-[var(--text)]">
            {stats?.totalRetrievals ?? 0}
          </div>
          <div className="text-[11px] text-[var(--text-3)] mt-1">
            Retrieved by search
          </div>
        </div>
        <div className="w-px bg-[var(--border-2)]" />
        <div className="flex-1">
          <div className="font-serif text-[26px] font-medium text-[var(--text)]">
            {stats?.uniqueConversations ?? 0}
          </div>
          <div className="text-[11px] text-[var(--text-3)] mt-1">
            Conversations
          </div>
        </div>
      </div>
      <p className="text-[11px] text-[var(--text-faint)]">
        Stats reflect all conversations in your organisation
      </p>
    </div>
  );
}

/**
 * Single chunk item with expand/collapse
 */
function ChunkItem({
  chunk,
  isExpanded,
  onToggle,
}: {
  chunk: DocumentChunksResponse["chunks"][number];
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="p-[13px_20px]">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[11px] font-mono font-semibold text-[var(--accent)] bg-[var(--accent-tint)] px-[7px] py-px rounded-md">
          #{chunk.chunkIndex}
        </span>
        <span
          className={`inline-block w-1.5 h-1.5 rounded-full ${
            chunk.isIndexed ? "bg-[var(--ok-dot)]" : "bg-[var(--text-faint)]"
          }`}
          title={chunk.isIndexed ? "Indexed" : "Not indexed"}
        />
        <span className="font-mono text-[11px] text-[var(--text-faint)]">
          {chunk.characterCount.toLocaleString()} chars
        </span>
      </div>
      <p
        className={`text-[12.5px] leading-[1.55] text-[var(--text-2)] whitespace-pre-wrap break-words ${
          isExpanded ? "" : "line-clamp-2"
        }`}
      >
        {chunk.content}
      </p>
      {chunk.content.length > 300 && (
        <button
          onClick={onToggle}
          className="mt-2 text-[11.5px] text-[var(--accent)] hover:underline"
        >
          {isExpanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

/**
 * Extracted chunks card with pagination
 */
function ChunkPreviewCard({ documentId }: { documentId: string }) {
  const [page, setPage] = useState(1);
  const [expandedChunks, setExpandedChunks] = useState<Set<string>>(new Set());
  const { data, isLoading, isError } = useDocumentChunks(documentId, page, 10);

  const toggleChunk = (chunkId: string) => {
    setExpandedChunks((prev) => {
      const next = new Set(prev);
      if (next.has(chunkId)) {
        next.delete(chunkId);
      } else {
        next.add(chunkId);
      }
      return next;
    });
  };

  if (isLoading) return <CardSkeleton rows={5} />;

  if (isError) {
    return (
      <div className={`${CARD} p-[18px_20px]`}>
        <h2 className="text-[14px] font-semibold text-[var(--text)] mb-2">
          Extracted chunks
        </h2>
        <p className="text-[12.5px] text-[var(--err-text)]">
          Failed to load chunks.
        </p>
      </div>
    );
  }

  if (!data) return null;

  const { chunks, pagination, summary } = data;

  return (
    <div className={`${CARD} overflow-hidden`}>
      {/* Header */}
      <div className="flex items-center justify-between p-[15px_20px] border-b border-[var(--border-2)]">
        <h2 className="text-[14px] font-semibold text-[var(--text)]">
          Extracted chunks
        </h2>
        <span className="font-mono text-[12px] text-[var(--text-faint)]">
          {summary.totalChunks} chunks · avg{" "}
          {summary.averageCharacterCount.toLocaleString()} chars
        </span>
      </div>

      {/* Chunk list */}
      <div className="divide-y divide-[var(--border-2)]">
        {chunks.map((chunk) => (
          <ChunkItem
            key={chunk.id}
            chunk={chunk}
            isExpanded={expandedChunks.has(chunk.id)}
            onToggle={() => toggleChunk(chunk.id)}
          />
        ))}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between p-[13px_20px] border-t border-[var(--border-2)]">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="inline-flex items-center gap-1 h-8 px-3 text-[12.5px] font-medium rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[var(--text-2)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Previous
          </button>
          <span className="text-[12px] text-[var(--text-faint)]">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() =>
              setPage((p) => Math.min(pagination.totalPages, p + 1))
            }
            disabled={page >= pagination.totalPages}
            className="inline-flex items-center gap-1 h-8 px-3 text-[12.5px] font-medium rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[var(--text-2)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Modal for uploading a new version of a document
 */
function UploadVersionModal({
  documentId,
  onClose,
}: {
  documentId: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { upload, isUploading, progress, error } = useUploadNewVersion();
  const { acceptString, formatsLabel } = useSupportedFormats();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    try {
      const result = await upload(documentId, selectedFile);
      navigate(`/documents/${result.documentId}`);
    } catch {
      // error is set in hook
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={!isUploading ? onClose : undefined}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className={`relative ${CARD} max-w-md w-full p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[16px] font-semibold text-[var(--text)]">
              Upload new version
            </h3>
            {!isUploading && (
              <button
                onClick={onClose}
                className="text-[var(--text-faint)] hover:text-[var(--text-2)]"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>

          <p className="text-[13px] leading-[1.6] text-[var(--text-2)] mb-4">
            Uploading a new version will archive the current document. Previous
            version chunks will no longer appear in search results.
          </p>

          <div className="space-y-4">
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptString}
                onChange={handleFileChange}
                className="hidden"
                disabled={isUploading}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="w-full px-4 py-3 border-2 border-dashed border-[var(--border)] rounded-[9px] text-[13px] text-[var(--text-2)] hover:border-[var(--accent)] hover:bg-[var(--surface-2)] disabled:opacity-50 transition-colors"
              >
                {selectedFile
                  ? selectedFile.name
                  : `Select file (${formatsLabel})`}
              </button>
            </div>

            {isUploading && (
              <div className="w-full bg-[var(--surface-2)] rounded-full h-2">
                <div
                  className="bg-[var(--accent)] h-2 rounded-full transition-all duration-200"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}

            {error && (
              <p className="text-[13px] text-[var(--err-text)]">{error}</p>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                disabled={isUploading}
                className="h-9 px-3.5 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] font-medium text-[var(--text-2)] hover:bg-[var(--surface-2)] disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
                className="h-9 px-[15px] rounded-[9px] bg-[var(--accent)] text-white text-[13px] font-semibold hover:bg-[var(--accent-strong)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isUploading ? "Uploading..." : "Upload new version"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Version history timeline card
 */
function VersionHistoryCard({
  versions,
  currentDocId,
}: {
  versions: VersionHistoryEntry[];
  currentDocId: string;
}) {
  if (versions.length <= 1) return null;

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className={`${CARD} p-[18px_20px]`}>
      <h2 className="text-[14px] font-semibold text-[var(--text)] mb-[18px]">
        Version history
      </h2>
      <div className="space-y-3">
        {versions.map((v) => (
          <div
            key={v.id}
            className={`flex items-center justify-between p-3 rounded-[9px] border ${
              v.id === currentDocId
                ? "border-[var(--accent)] bg-[var(--accent-tint)]"
                : "border-[var(--border-2)]"
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-mono font-semibold bg-[var(--surface-2)] text-[var(--text-2)]">
                v{v.versionNumber}
              </span>
              <div className="min-w-0">
                {v.id === currentDocId ? (
                  <span className="text-[13px] font-medium text-[var(--text)] truncate block">
                    {v.filename}
                  </span>
                ) : (
                  <Link
                    to={`/documents/${v.id}`}
                    className="text-[13px] font-medium text-[var(--accent)] hover:underline truncate block"
                  >
                    {v.filename}
                  </Link>
                )}
                <span className="text-[11px] text-[var(--text-faint)]">
                  {format(new Date(v.createdAt), "MMM d, yyyy")} &middot;{" "}
                  {formatSize(v.sizeBytes)} &middot; {v.chunkCount} chunks
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {v.isCurrent && (
                <span className="text-[11px] text-[var(--ok-text)] font-medium">
                  Current
                </span>
              )}
              {v.status === "ARCHIVED" && (
                <span className="text-[11px] text-[var(--text-faint)]">
                  Archived
                </span>
              )}
              <StatusPill status={v.status as DocumentStatus} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Document details page with status, info, timeline, retrieval stats, and chunks
 */
export function DocumentDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { document, isLoading, isError } = useDocument(id || "");
  const { versions } = useVersionHistory(id || "");
  const [versionModalOpen, setVersionModalOpen] = useState(false);

  const [isDownloading, setIsDownloading] = useState(false);
  const utils = trpc.useContext();

  // Phase 14: self-service reprocess (admin-only; eligibility from the server)
  const { isAdmin, isOwner, isSuperAdmin } = usePermissions();
  const canManageReprocess = isAdmin || isOwner || isSuperAdmin;
  const { reprocessDocument, isLoading: isReprocessing } =
    useReprocessDocument();
  const [reprocessError, setReprocessError] = useState<string | null>(null);

  const handleReprocess = async () => {
    if (!id || isReprocessing) return;
    setReprocessError(null);
    const res = await reprocessDocument(id);
    if (!res.success) setReprocessError(res.error ?? "Failed to reprocess");
  };

  const handleDownload = async () => {
    if (!id || isDownloading) return;
    setIsDownloading(true);
    try {
      // @ts-ignore - tRPC type inference through `as any` router cast
      const dl = await utils.client.documents.getDownloadUrl.query({ id });
      if (dl?.url) {
        window.open(dl.url, "_blank");
      }
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-[13px] text-[var(--text-3)]">
              Loading document...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !document) {
    return (
      <div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="rounded-[14px] border border-[var(--err-text)] bg-[var(--err-bg)] p-4">
            <p className="text-[13px] text-[var(--err-text)]">
              Failed to load document. Please try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const nextAction = getNextAction(document.status, navigate);
  const timelineStages = getTimelineStages(document.status);

  // Phase 14: failure/health flags for the reprocess affordance
  const needsReprocess =
    document.status === "FAILED" ||
    document.status === "PARTIALLY_INDEXED" ||
    document.chunkHealth === "SUSPECT";
  const reprocessStruckOut = (document.reprocessFailureCount ?? 0) >= 3;

  // Derive current version number (if version history is available) for the id line.
  const currentVersion = versions.find((v) => v.id === document.id);
  const extractionStrategy = (document as any).extractionStrategy;
  const focusAreas: string[] =
    extractionStrategy?.extractorConfig?.focusAreas ?? [];

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back link */}
        <button
          onClick={() => navigate("/documents")}
          className="inline-flex items-center gap-1.5 text-[12.5px] text-[var(--text-3)] mb-[14px] hover:text-[var(--text-2)] transition-colors"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Back to documents
        </button>

        {/* Header */}
        <div className="mb-[22px]">
          <h1 className="font-serif text-[27px] font-medium text-[var(--text)] tracking-[-0.01em] leading-[1.22] max-w-[820px]">
            {document.title || document.filename}
          </h1>
          <div className="flex items-center justify-between gap-4 flex-wrap mt-3">
            {/* Left: status pill + mono id line */}
            <div className="flex items-center gap-3 flex-wrap">
              <StatusPill status={document.status} />
              {document.chunkHealth === "SUSPECT" &&
                document.status !== "FAILED" && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[var(--warn-bg)] text-[var(--warn-text)]">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    Suspect chunking
                  </span>
                )}
              <span className="text-[12px] text-[var(--text-faint)] font-mono">
                {document.id}
                {currentVersion ? ` · v${currentVersion.versionNumber}` : ""}
              </span>
            </div>

            {/* Right: action buttons */}
            <div className="flex gap-2">
              {/* Secondary: Download */}
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className="h-9 px-3.5 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] font-medium text-[var(--text-2)] inline-flex items-center gap-2 hover:bg-[var(--surface-2)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                {isDownloading ? "Preparing..." : "Download"}
              </button>
              {/* Phase 14: self-service reprocess (admin-only) */}
              {canManageReprocess && document.canReprocess && (
                <button
                  onClick={handleReprocess}
                  disabled={isReprocessing}
                  className="h-9 px-3.5 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] font-medium text-[var(--text-2)] inline-flex items-center gap-2 hover:bg-[var(--surface-2)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ArrowPathIcon
                    className={`h-4 w-4 ${isReprocessing ? "animate-spin" : ""}`}
                  />
                  {isReprocessing ? "Reprocessing..." : "Reprocess"}
                </button>
              )}
              {canManageReprocess &&
                needsReprocess &&
                reprocessStruckOut &&
                !document.canReprocess && (
                  <span className="h-9 px-3.5 rounded-[9px] border border-[var(--err-text)] bg-[var(--err-bg)] text-[13px] font-medium text-[var(--err-text)] inline-flex items-center gap-2">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    Reprocessing unavailable — contact support
                  </span>
                )}
              {document.status === "INDEXED" && (
                <button
                  onClick={() => setVersionModalOpen(true)}
                  className="h-9 px-3.5 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] font-medium text-[var(--text-2)] inline-flex items-center gap-2 hover:bg-[var(--surface-2)] transition-colors"
                >
                  <ArrowUpTrayIcon className="h-4 w-4" />
                  New version
                </button>
              )}
              {nextAction &&
                (nextAction.variant === "primary" ? (
                  <button
                    onClick={nextAction.onClick}
                    className="h-9 px-[15px] rounded-[9px] bg-[var(--accent)] text-white text-[13px] font-semibold inline-flex items-center gap-2 hover:bg-[var(--accent-strong)] transition-colors"
                  >
                    <MagnifyingGlassIcon className="h-4 w-4" />
                    {nextAction.label}
                  </button>
                ) : (
                  <button
                    onClick={nextAction.onClick}
                    className="h-9 px-3.5 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] font-medium text-[var(--text-2)] inline-flex items-center gap-2 hover:bg-[var(--surface-2)] transition-colors"
                  >
                    <ArrowUpTrayIcon className="h-4 w-4" />
                    {nextAction.label}
                  </button>
                ))}
            </div>
          </div>

          {/* Status description + reprocess guidance (preserved from status card) */}
          <p className="mt-3 text-[13px] leading-[1.6] text-[var(--text-2)] max-w-[820px]">
            {getStatusDescription(document.status)}
          </p>
          {document.status === "FAILED" && document.failureReason && (
            <p className="mt-2 max-w-[820px] break-words rounded-lg border border-[var(--err-text)] bg-[var(--err-bg)] px-3 py-2 font-mono text-[12px] leading-[1.5] text-[var(--err-text)]">
              {document.failureReason}
            </p>
          )}
          {needsReprocess && (
            <p className="mt-2 text-[13px] text-[var(--warn-text)]">
              This document didn't index cleanly
              {canManageReprocess
                ? document.canReprocess
                  ? " — use Reprocess above to re-run it through the pipeline."
                  : reprocessStruckOut
                    ? " and has hit the retry limit. Please contact support."
                    : "."
                : ". An organization admin can reprocess it."}
            </p>
          )}
          {reprocessError && (
            <p className="mt-2 text-[13px] text-[var(--err-text)]">
              {reprocessError}
            </p>
          )}
        </div>

        {/* AI Summary */}
        {document.summary && (
          <div className={`${CARD} p-[18px_20px] mb-4`}>
            <div className="flex items-center gap-2 mb-2.5">
              <span className="text-[10.5px] font-semibold tracking-[0.08em] text-[var(--text-3)]">
                SUMMARY
              </span>
              <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-[var(--accent-tint)] text-[var(--accent)]">
                AI GENERATED
              </span>
            </div>
            <p className="text-[14px] leading-[1.65] text-[var(--text-2)] max-w-[880px]">
              {document.summary}
            </p>
          </div>
        )}

        {/* Two-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.7fr_1fr] gap-4 items-start">
          {/* LEFT column */}
          <div className="flex flex-col gap-4">
            {/* Processing timeline */}
            <div className={`${CARD} p-[18px_20px]`}>
              <h2 className="text-[14px] font-semibold text-[var(--text)] mb-[18px]">
                Processing timeline
              </h2>
              <div className="flex items-start">
                {timelineStages.map((stage, index) => (
                  <div
                    key={stage.key}
                    className="flex items-start"
                    style={{ flex: index === 0 ? "0 0 auto" : "1 1 0%" }}
                  >
                    {index > 0 && (
                      <div
                        className={`mt-[12px] h-0.5 flex-1 min-w-[16px] ${
                          stage.completed || stage.current
                            ? "bg-[var(--ok-dot)]"
                            : "bg-[var(--border-2)]"
                        }`}
                      />
                    )}
                    <div className="flex flex-col items-center gap-1.5">
                      <div
                        className={`w-[26px] h-[26px] rounded-full flex items-center justify-center ${
                          stage.current
                            ? "bg-[var(--accent)] text-white"
                            : stage.completed
                              ? "bg-[var(--ok-bg)] text-[var(--ok-text)]"
                              : "bg-[var(--surface-2)] text-[var(--text-3)]"
                        }`}
                      >
                        {stage.completed ? (
                          <CheckIcon className="h-3.5 w-3.5" />
                        ) : (
                          <span className="text-[11px] font-semibold">
                            {index + 1}
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] text-[var(--text-2)] text-center">
                        {stage.label}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Extracted chunks */}
            {document.chunkCount != null && document.chunkCount > 0 ? (
              <ChunkPreviewCard documentId={document.id} />
            ) : (
              <div className={`${CARD} p-[18px_20px]`}>
                <h2 className="text-[14px] font-semibold text-[var(--text)] mb-2">
                  Extracted chunks
                </h2>
                <p className="text-[12.5px] text-[var(--text-faint)]">
                  Chunks will appear here once processing is complete.
                </p>
              </div>
            )}
          </div>

          {/* RIGHT column */}
          <div className="flex flex-col gap-4">
            {/* Document information */}
            <div className={`${CARD} p-[16px_18px]`}>
              <h2 className="text-[14px] font-semibold text-[var(--text)] mb-[10px]">
                Document information
              </h2>
              {/* Filename — full-width stacked row */}
              <div className="mb-1 pb-2 border-b border-[var(--border-2)]">
                <div className="text-[11px] text-[var(--text-3)] tracking-[0.04em]">
                  FILENAME
                </div>
                <div className="font-mono text-[12.5px] text-[var(--text)] break-all mt-1">
                  {document.filename}
                </div>
              </div>
              <div className="flex justify-between gap-3 py-2 border-b border-[var(--border-2)]">
                <span className="text-[12.5px] text-[var(--text-3)]">
                  File size
                </span>
                <span className="font-mono text-[12.5px] text-[var(--text)]">
                  {formatFileSize(document.sizeBytes)}
                </span>
              </div>
              <div className="flex justify-between gap-3 py-2 border-b border-[var(--border-2)]">
                <span className="text-[12.5px] text-[var(--text-3)]">
                  File type
                </span>
                <span className="font-mono text-[12.5px] text-[var(--text)] break-all text-right">
                  {document.mimeType}
                </span>
              </div>
              <div className="flex justify-between gap-3 py-2 border-b border-[var(--border-2)]">
                <span className="text-[12.5px] text-[var(--text-3)]">
                  Chunks
                </span>
                <span className="font-mono text-[12.5px] text-[var(--text)]">
                  {document.chunkCount !== null
                    ? document.chunkCount
                    : "Not yet processed"}
                </span>
              </div>
              <div className="flex justify-between gap-3 py-2 border-b border-[var(--border-2)]">
                <span className="text-[12.5px] text-[var(--text-3)]">
                  Uploaded
                </span>
                <span className="font-mono text-[12.5px] text-[var(--text)] text-right">
                  {format(new Date(document.createdAt), "MMM d, yyyy h:mm a")}
                </span>
              </div>
              <div className="flex justify-between gap-3 py-2">
                <span className="text-[12.5px] text-[var(--text-3)]">
                  Last updated
                </span>
                <span className="font-mono text-[12.5px] text-[var(--text)] text-right">
                  {format(new Date(document.updatedAt), "MMM d, yyyy h:mm a")}
                </span>
              </div>
            </div>

            {/* Extraction strategy */}
            {extractionStrategy && (
              <div className={`${CARD} p-[16px_18px]`}>
                <h2 className="text-[14px] font-semibold text-[var(--text)] mb-[10px]">
                  Extraction strategy
                </h2>
                <div className="flex justify-between gap-3 py-2 border-b border-[var(--border-2)]">
                  <span className="text-[12.5px] text-[var(--text-3)]">
                    Strategy
                  </span>
                  <span className="text-[12.5px] text-[var(--text)] text-right">
                    {extractionStrategy.name}
                  </span>
                </div>
                <div className="flex justify-between gap-3 py-2 border-b border-[var(--border-2)]">
                  <span className="text-[12.5px] text-[var(--text-3)]">
                    Parser
                  </span>
                  <span className="font-mono text-[12.5px] text-[var(--text)] text-right">
                    {extractionStrategy.parserType}
                  </span>
                </div>
                <div className="flex justify-between gap-3 py-2 border-b border-[var(--border-2)]">
                  <span className="text-[12.5px] text-[var(--text-3)]">
                    Extractor
                  </span>
                  <span className="font-mono text-[12.5px] text-[var(--text)] text-right">
                    {extractionStrategy.extractorType}
                  </span>
                </div>
                <div
                  className={`flex justify-between gap-3 py-2 ${
                    focusAreas.length > 0
                      ? "border-b border-[var(--border-2)]"
                      : ""
                  }`}
                >
                  <span className="text-[12.5px] text-[var(--text-3)]">
                    Chunking
                  </span>
                  <span className="font-mono text-[12.5px] text-[var(--text)] text-right">
                    {extractionStrategy.chunkingType}
                  </span>
                </div>
                {focusAreas.length > 0 && (
                  <div className="pt-2.5">
                    <div className="text-[11px] text-[var(--text-3)] tracking-[0.04em] mb-1.5">
                      FOCUS AREAS
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {focusAreas.map((area: string) => (
                        <span
                          key={area}
                          className="text-[11.5px] px-[9px] py-[3px] rounded-[7px] bg-[var(--surface-2)] border border-[var(--border)] text-[var(--text-2)]"
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Retrieval activity */}
            {document.status === "INDEXED" ? (
              <RetrievalStatsCard documentId={document.id} />
            ) : (
              <div className={`${CARD} p-[16px_18px]`}>
                <h2 className="text-[14px] font-semibold text-[var(--text)] mb-[10px]">
                  Retrieval activity
                </h2>
                <p className="text-[12.5px] text-[var(--text-faint)]">
                  Retrieval stats will appear once the document is indexed.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Version history */}
        {versions.length > 1 && (
          <div className="mt-4">
            <VersionHistoryCard
              versions={versions}
              currentDocId={document.id}
            />
          </div>
        )}
      </div>

      {/* Version upload modal */}
      {versionModalOpen && (
        <UploadVersionModal
          documentId={document.id}
          onClose={() => setVersionModalOpen(false)}
        />
      )}
    </div>
  );
}
